import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, KeyRound } from 'lucide-react';
import { portalApi, portalAuth } from '@/lib/portalApi';

export default function PortalAcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const inviteToken = params.get('token');

  useEffect(() => {
    if (!inviteToken) {
      toast.error('Invite token missing');
      navigate('/portal/login', { replace: true });
    }
  }, [inviteToken, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await portalApi.acceptInvite(inviteToken, password);
      portalAuth.set(res.data.token, res.data.user);
      toast.success('Account activated');
      navigate(res.data.user.portalRole === 'family_principal' ? '/portal/family' : '/portal/adviser', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired invite');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F9F7F5] via-white to-[#FFF8F0] p-4" data-testid="portal-accept-page">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-[#5D524D] hover:text-[#2C2420] mb-6"
          data-testid="portal-accept-back"
        >
          ← Back to QIndex One
        </Link>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#A86A3A] to-[#4A3B32] mb-3">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-2xl text-[#2C2420] mb-2">Set Your Password</h1>
          <p className="text-sm text-[#8C8078]">Activate your QIndex One portal account</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E6E0D9] p-6 space-y-4 shadow-sm">
          <div className="space-y-1.5">
            <Label htmlFor="pw">New password</Label>
            <Input
              id="pw"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              minLength={8}
              required
              data-testid="accept-password-input"
            />
            <p className="text-[10px] text-[#8C8078]">Minimum 8 characters</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw2">Confirm password</Label>
            <Input
              id="pw2"
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
              data-testid="accept-confirm-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#4A3B32] text-white hover:bg-[#3a2e26]"
            data-testid="accept-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Activate Account
          </Button>
        </form>

        <p className="text-center text-xs text-[#8C8078] mt-6">
          Already have an account? <Link to="/portal/login" className="text-[#A86A3A] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
